import { TextInput } from "@carbon/react";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { ConfirmActionModal } from "src/components/Modal";
import { PropertyFilterableSinglePickerFormField } from "src/components/Pickers/PropertyFilterableSinglePickerFormField";
import { useT } from "src/internationalization";
import { SecondaryCriteria } from "src/pages/Rules/linkedCase/SecondaryCriteria/SecondaryCriteria.types";
import { ClientSchemaProperty } from "src/types/clientSchema";
import { Operator } from "src/types/operator";

import { OperatorFilterableSinglePickerFormField } from "./OperatorFilterableSinglePickerFormField";

type FormData = {
  secondProperty: ClientSchemaProperty | null;
  operator: Operator | null;
  value: string | null;
};

type SecondaryCriteriaFormProps = {
  onSave: (data: Omit<SecondaryCriteria, "id">) => void;
  onCancel: () => void;
  isEditing: boolean;
  initialData?: SecondaryCriteria;
  caseTypeCode?: string;
};

export const SecondaryCriteriaModal: React.FC<SecondaryCriteriaFormProps> = ({
  onSave,
  onCancel,
  isEditing,
  initialData,
  caseTypeCode,
}) => {
  const t = useT();

  const methods = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      operator: null,
      secondProperty: null,
      ...initialData,
    },
  });

  useEffect(() => {
    if (initialData) {
      methods.reset({
        ...initialData,
      });
    }
  }, [initialData, methods]);

  const secondProperty = methods.watch("secondProperty");
  const operator = methods.watch("operator");

  useEffect(() => {
    if (!secondProperty) {
      methods.setValue("operator", null);
      methods.setValue("value", null);
    }
  }, [secondProperty, methods]);

  const handlePartialSubmit = async () => {
    const isValidProperty = await methods.trigger("secondProperty");
    const isValidOperator = await methods.trigger("operator");
    const needsValueValidation = operator?.operatorValueType === "argument";
    const isValidValue = !needsValueValidation || (await methods.trigger("value"));

    if (isValidProperty && isValidOperator && isValidValue) {
      const data = methods.getValues();
      const saveData: Omit<SecondaryCriteria, "id"> = {
        secondProperty: data.secondProperty!,
        operator: data.operator,
      };

      if (data.operator?.operatorValueType === "argument") {
        saveData.value = data.value;
      }

      onSave(saveData);
    }
  };

  return (
    <ConfirmActionModal
      onClose={onCancel}
      title={t("page.rules.modal.create.linkedCase.secondary.criteria.title")}
      confirmButtonText={isEditing ? t("general.actions.edit") : t("general.actions.add")}
      onConfirm={handlePartialSubmit}
      size="md"
      closeOnEscape={false}
      closeOnOutsideClick={false}
      initialFocus="#secondProperty"
    >
      <FormProvider {...methods}>
        <div className="flex flex-col gap-5 my-4">
          <div className="flex gap-5">
            <div className="flex-1">
              <PropertyFilterableSinglePickerFormField
                id="secondProperty"
                name="secondProperty"
                rules={{ required: t("general.form.field.required") }}
                label={t(
                  "page.rules.modal.create.linkedCase.form.secondary.criteria.property.title"
                )}
                caseTypeCode={caseTypeCode}
              />
            </div>
            <div className="flex-1">
              <OperatorFilterableSinglePickerFormField
                id="operatorPicker"
                name="operator"
                rules={{ required: t("general.form.field.required") }}
                label={t(
                  "page.rules.modal.create.linkedCase.form.secondary.criteria.operator.title"
                )}
                dataType={secondProperty?.dataType}
              />
            </div>
            {operator?.operatorValueType === "argument" && (
              <div className="flex-1">
                <TextInput
                  id="value"
                  labelText={t(
                    "page.rules.modal.create.linkedCase.form.secondary.criteria.operator.value"
                  )}
                  invalid={!!methods.formState.errors.value}
                  invalidText={t("general.form.field.required")}
                  defaultValue={initialData?.value ?? undefined}
                  {...methods.register("value", { required: true })}
                />
              </div>
            )}
          </div>
        </div>
      </FormProvider>
    </ConfirmActionModal>
  );
};
